import {ToastNotification, ActionableNotification} from '@carbon/react';
import {observer} from 'mobx-react';
import {useContext} from 'react';
import {NetworkContext} from '../../lib/context';
import {shouldDisplayErrorId} from '../../../../lib/error-handler';

/**
 * Shared toast notification component for Network Optimization module
 *
 * Displays a queue of notifications, each can be either:
 * - ActionableNotification: When a retry action is available (e.g., 5xx errors)
 * - ToastNotification: For simple messages without actions
 *
 * Error IDs are automatically appended to messages for support tracking
 * Notifications are stacked vertically with 8rem spacing to prevent overlap
 */
const ToastNotifications = observer(() => {
    const store = useContext(NetworkContext);

    if (store.notifications.length === 0) return null;

    return (
        <>
            {store.notifications.map((notification, index) => {
                const commonProps = {
                    'aria-label': 'closes notification',
                    onClose: () => store.hideToastNotification(notification.id),
                    onCloseButtonClick: () => store.hideToastNotification(notification.id),
                    kind: notification.kind,
                    title: notification.title,
                    role: 'alert' as const,
                    statusIconDescription: 'notification',
                    style: {
                        position: 'fixed' as const,
                        top: `${3 + index * 8}rem`,
                        right: '1rem',
                        zIndex: 9999 - index,
                        minWidth: '350px',
                        maxWidth: '500px'
                    }
                };

                // ActionableNotification for errors with retry actions
                if (notification.action) {
                    return (
                        <ActionableNotification
                            key={notification.id}
                            {...commonProps}
                            actionButtonLabel={notification.action.label}
                            onActionButtonClick={() => {
                                notification.action?.onClick();
                                store.hideToastNotification(notification.id);
                            }}
                            subtitle={
                                notification.subtitle +
                                (shouldDisplayErrorId(notification.errorId)
                                    ? ` (Error ID: ${notification.errorId})`
                                    : '')
                            }
                            inline={false}
                        />
                    );
                }

                // ToastNotification for simple messages
                return (
                    <ToastNotification
                        key={notification.id}
                        {...commonProps}
                        caption={shouldDisplayErrorId(notification.errorId) ? `Error ID: ${notification.errorId}` : ''}
                        subtitle={notification.subtitle}
                        timeout={notification.timeout ?? (notification.kind === 'error' ? 30000 : 5000)}
                    />
                );
            })}
        </>
    );
});

export default ToastNotifications;
